import axios from "axios";
import { Box, Button, TextField, Typography } from "@mui/material";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import { FilterList } from "@mui/icons-material";
import { MovementTypeEnum } from "../../../common/EntitiesManagement";
import { useState } from "react";
import useGlobalContext from "../../../hooks/useGlobalContext";

export default function InventoryMovementFilter({ fullData, setDataFiltered }) {
  const { WAREHOUSE_ENDPOINT, INVENTORY_MOVEMENT_TYPE_ENDPOINT, config } =
    useGlobalContext();
  const [selectedFilter, setSelectedFilter] = useState(undefined);
  const [selectedFilterValue, setSelectedFilterValue] = useState(undefined);
  const [filterOptions, setFilterOptions] = useState(undefined);
  const [filterValue, setFilterValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const Filters = [
    {
      Id: "CODE",
      Name: localize.code,
    },
    {
      Id: "TYPE",
      Name: localize.movement_type,
    },
    {
      Id: "MOVEMENT",
      Name: localize.movement,
    },
    {
      Id: "WAREHOUSE",
      Name: localize.warehouse,
    },
  ];

  const movementFilterOptions = [
    {
      Id: MovementTypeEnum.In,
      Name: localize.in,
    },
    {
      Id: MovementTypeEnum.Out,
      Name: localize.out,
    },
  ];

  const getDataFromApi = async (endpointName) => {
    setIsLoading(true);

    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${endpointName}/`, requestConfig);

    setFilterOptions(
      data?.data?.Data?.map((x) => ({
        Id: x.Id,
        Name: x.Name,
      }))
    );
    setIsLoading(false);
  };

  const getMovementTypes = () => {
    getDataFromApi(INVENTORY_MOVEMENT_TYPE_ENDPOINT);
  };

  const getWarehouses = () => {
    getDataFromApi(WAREHOUSE_ENDPOINT);
  };

  const handleFilterValueSelection = (value) => {
    setSelectedFilterValue(value);
    setDataFiltered(fullData);
    if (value) {
      setFilterValue(value?.Id + "");
      return;
    }
    setFilterValue(undefined);
  };

  const handleFilterSelection = (filter) => {
    setIsLoading(false);
    setFilterValue("");
    setSelectedFilterValue(undefined);
    setSelectedFilter(filter);

    if (filter) {
      switch (filter.Id) {
        case "TYPE":
          getMovementTypes();
          break;

        case "MOVEMENT":
          setFilterOptions(movementFilterOptions);
          break;

        case "WAREHOUSE":
          getWarehouses();
          break;
      }

      return;
    }

    setDataFiltered(fullData);
  };

  const handleFilterBtn = () => {
    switch (selectedFilter.Id) {
      case "CODE":
        const filteredByCode = fullData?.filter(
          (x) => x.Code.toString().toLowerCase() === filterValue.toLowerCase()
        );
        setDataFiltered && setDataFiltered(filteredByCode);
        break;

      case "TYPE":
        const filteredByType = fullData?.filter(
          (x) => x.MovementType?.Id === selectedFilterValue.Id
        );
        setDataFiltered && setDataFiltered(filteredByType);
        break;

      case "MOVEMENT":
        const filteredByMovement = fullData?.filter(
          (x) => x.MovementType?.MovementType === selectedFilterValue.Id
        );
        setDataFiltered(filteredByMovement);
        break;

      case "WAREHOUSE":
        const filteredByWarehouse = fullData?.filter(
          (x) => x.Warehouse?.Id === selectedFilterValue.Id
        );
        setDataFiltered && setDataFiltered(filteredByWarehouse);
        break;
    }
  };

  const handleFilterValueChange = (value) => {
    setFilterValue(value.target.value);
  };

  const keyUp = (e) => {
    if (e.key === "Enter") {
      handleFilterBtn();
    }
  };

  const getFilterValueComponent = () => {
    if (isLoading) {
      return <Typography className="mt-2">{localize.loading}...</Typography>;
    }

    if (selectedFilter) {
      switch (selectedFilter.Id) {
        case "CODE":
          return (
            <Box className="flex gap-2 w-full mt-2">
              <Box className="flex gap-2 items-center w-full">
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  name="FilterValue"
                  label={selectedFilter.Name}
                  variant="outlined"
                  onChange={handleFilterValueChange}
                  value={filterValue}
                  onKeyUp={keyUp}
                />

                <Button
                  style={{ width: "150px" }}
                  disabled={
                    (!selectedFilterValue && !filterValue) ||
                    filterValue?.replaceAll(" ", "").length === 0
                  }
                  size="small"
                  variant="contained"
                  onClick={handleFilterBtn}
                  className="btn app_button app_general_button_data btn-sm"
                >
                  <span className="mr-2">{localize.filter}</span>
                  <FilterList className="text-xs" />
                </Button>
              </Box>
            </Box>
          );

        default:
          return (
            <Box className="flex gap-2 items-center w-full mt-2">
              <Combobox
                id="Filter"
                handleValueChange={handleFilterValueSelection}
                fieldValue={selectedFilterValue}
                fieldOptions={filterOptions ?? []}
                fieldLabel={localize.movement}
              />

              <Button
                style={{ width: "150px" }}
                disabled={
                  !filterValue || filterValue.replaceAll(" ", "").length === 0
                }
                size="small"
                variant="contained"
                onClick={handleFilterBtn}
                className="btn app_button app_general_button_data btn-sm"
              >
                <span className="mr-2">{localize.filter}</span>
                <FilterList className="text-xs" />
              </Button>
            </Box>
          );
      }
    }
  };

  return (
    <Box className="flex flex-col gap-3 w-full mt-3">
      <Box className="w-full">
        <Combobox
          id="Filter"
          handleValueChange={handleFilterSelection}
          fieldValue={selectedFilter}
          fieldOptions={Filters ?? []}
          fieldLabel={localize.filter_list}
        />
        {getFilterValueComponent()}
      </Box>
    </Box>
  );
}
